import { AgChartOptions } from "ag-charts-community";

import { ANALYTICS_CUSTOM_PRIMARY_COLOR } from "../constants";

import { baseNonCartesianAnalyticsChartOptions } from "./base";
import { createAnalyticsTooltipResult, formatAxisValue } from "./customChartFormatters";

import {
  CustomChartType,
  type CustomChartDataPoint,
  type CustomChartResponseItem,
} from "@/queries/analytics-custom-charts-types";

interface HierarchyNode {
  label: string;
  value: number;
  children?: HierarchyNode[];
}

const buildHierarchyTree = (rows: CustomChartDataPoint[]): HierarchyNode[] => {
  const roots: HierarchyNode[] = [];
  const nodeIndex = new Map<string, HierarchyNode>();

  rows.forEach(row => {
    const path = row.path ?? [row.label];
    const leafValue = Number(row.value ?? 0);
    let currentPath = "";
    let siblings = roots;

    path.forEach((segment, index) => {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      let node = nodeIndex.get(currentPath);
      if (!node) {
        node = {
          label: segment,
          value: 0,
          children: index < path.length - 1 ? [] : undefined,
        };
        nodeIndex.set(currentPath, node);
        siblings.push(node);
      }
      if (index === path.length - 1) {
        node.value = leafValue;
      }
      siblings = node.children ?? [];
    });
  });

  const rollUpValues = (node: HierarchyNode): number => {
    if (!node.children || node.children.length === 0) {
      return node.value;
    }
    node.value = node.children.reduce((total, child) => total + rollUpValues(child), 0);
    return node.value;
  };

  roots.forEach(rollUpValues);
  return roots;
};

export const buildHierarchyCustomChartOptions = (
  chart: CustomChartResponseItem,
): AgChartOptions => {
  const unit = chart.series[0]?.unit ?? "number";
  const valueLabel = chart.series[0]?.label ?? "Value";
  const formatValue = (value: number) => formatAxisValue(unit, value);
  const data = buildHierarchyTree(chart.data);
  const isSunburst = chart.chartType === CustomChartType.Sunburst;

  return {
    ...baseNonCartesianAnalyticsChartOptions,
    data,
    series: [
      {
        type: isSunburst ? ("sunburst" as const) : ("treemap" as const),
        labelKey: "label",
        sizeKey: "value",
        childrenKey: "children",
        tooltip: {
          renderer: ({ datum }: { datum: HierarchyNode }) =>
            createAnalyticsTooltipResult({
              title: datum.label,
              symbolColor: ANALYTICS_CUSTOM_PRIMARY_COLOR,
              rows: [
                {
                  label: valueLabel,
                  value: formatValue(Number(datum.value ?? 0)),
                  color: ANALYTICS_CUSTOM_PRIMARY_COLOR,
                },
              ],
            }),
        },
      },
    ],
    legend: {
      enabled: false,
    },
  } as AgChartOptions;
};
